<template>
  <div class="grid mt-1">
    <div class="col-12">
      <div class="movimento-ignoto-info mb-3">
        <div>
          <span class="block text-500 font-medium mb-1 title-detail">ASSOCIAZIONE MANUALE</span>
          <h5 class="mt-0 mb-2">Movimento senza riferimenti riconoscibili</h5>
        </div>
        <i class="pi pi-question-circle text-orange-500 text-2xl"></i>
      </div>

      <small v-if="loadingSuggestions" class="block mt-2 text-600">
        Ricerca dei possibili suggerimenti in corso...
      </small>
      <small v-else-if="hasSuggestions && suggestions.length === 1" class="block mt-2 text-green-600">
        È stato trovato un solo suggerimento: puoi associarlo direttamente oppure salvare il movimento come movimento ignoto.
      </small>
      <small v-else-if="!hasSuggestions" class="block mt-2 text-orange-600">
        Nessun suggerimento trovato: puoi comunque salvare il movimento come movimento ignoto.
      </small>

      <div v-if="hasSuggestions" class="movimento-ignoto-suggestions mt-3">
        <h5 class="mt-0 mb-2">Possibili associazioni</h5>
        <div v-for="suggestion in suggestions" :key="suggestionKey(suggestion)" class="suggestion-card mb-2">
          <div class="flex justify-content-between align-items-start gap-2">
            <div>
              <span class="block text-500 text-sm">{{ suggestion.natura === 'fattura' ? 'Fattura' : `Rata dilazione #${suggestion.id_rata}` }}</span>
              <a
                v-if="getClientName(suggestion)"
                :href="inCallUrl(suggestion)"
                class="client-incall-link"
                target="_blank"
                rel="noopener noreferrer"
                title="Apri il cliente in In Call"
              >
                <i class="pi pi-external-link"></i>
                {{ getClientName(suggestion) }}
              </a>
              <strong v-else>Cliente non disponibile</strong>
            </div>
            <span class="text-500 text-sm">
              <span class="block">{{ suggestion.natura === 'fattura' ? suggestion.numero_fattura : `Fatture: ${suggestion.numero_fattura}` }}</span>
              <strong class="text-blue-500">{{ Number(suggestion.importo).toFixed(2) }} EUR</strong>
            </span>
          </div>
          <div class="suggestion-details mt-2">
            <span v-if="suggestion.rappresentante"><i class="pi pi-user mr-1"></i>{{ suggestion.rappresentante }}</span>
            <span v-if="suggestion.cliente_cellulare"><i class="pi pi-phone mr-1"></i>{{ suggestion.cliente_cellulare }}</span>
            <span v-if="suggestion.cliente_email"><i class="pi pi-envelope mr-1"></i>{{ suggestion.cliente_email }}</span>
          </div>
          <div class="mt-3 text-right">
            <Button
              label="Associa movimento"
              icon="pi pi-link"
              class="p-button-sm p-button-success p-button-rounded"
              :loading="directAssociating === suggestionKey(suggestion)"
              :disabled="saving || directAssociating !== null"
              @click="associateSuggestion(suggestion)"
            />
          </div>
        </div>
      </div>

      <div v-if="!loadingSuggestions" class="mt-3 text-right">
        <Button
          @click="associa"
          label="Salva come movimento ignoto"
          icon="pi pi-save"
          class="p-button-outlined p-button-primary"
          :disabled="saving || loadingSuggestions || directAssociating !== null"
          :loading="saving || loadingSuggestions"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance, onMounted, ref } from 'vue'
import Service from '@/services/ask-pratiche/pratiche-service'

export default {
  emits: ['movement-regularized'],
  props: {
    detail: {
      type: Object,
      required: true
    }
  },
  setup(props, { emit }) {
    const self = getCurrentInstance()
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    self.$http = self.appContext.config.globalProperties.$http
    const service = new Service(self.$http, self.$eventBus)
    const saving = ref(false)
    const directAssociating = ref(null)
    const loadingSuggestions = ref(true)
    const hasSuggestions = ref(false)
    const suggestions = ref([])

    onMounted(async () => {
      try {
        const response = await service.searchMovimentoIgnotoSuggestions(props.detail)
        suggestions.value = Array.isArray(response?.items) ? response.items : []
        hasSuggestions.value = suggestions.value.length > 0
      } finally {
        loadingSuggestions.value = false
      }
    })

    const getClientName = (suggestion) => {
      if (suggestion.cliente_ragsoc) return suggestion.cliente_ragsoc
      return [suggestion.cliente_nome, suggestion.cliente_cognome].filter(Boolean).join(' ')
    }

    const inCallUrl = (suggestion) => `/in-call?search_type=cliente&q=${encodeURIComponent(getClientName(suggestion))}`

    const suggestionKey = (suggestion) => `${suggestion.natura}-${suggestion.id_rata || suggestion.numero_fattura}`

    const associa = async () => {
      if (saving.value) return

      saving.value = true
      try {
        const response = await service.associateToMovimentoIgnoto(props.detail)
        if (response) {
          emit('movement-regularized', { id: props.detail.id })
        }
      } finally {
        saving.value = false
      }
    }

    const associateSuggestion = async (suggestion) => {
      if (saving.value || directAssociating.value !== null) return

      directAssociating.value = suggestionKey(suggestion)
      try {
        const payments = Array.isArray(suggestion.fatture) && suggestion.fatture.length
          ? suggestion.fatture.map((invoice) => ({
              typeAssociate: 'fattura-vendita',
              numero: invoice.numero,
              saldo_modificato: Number(invoice.saldo_modificato)
            }))
          : [{
              typeAssociate: 'fattura-vendita',
              numero: suggestion.numero_fattura,
              saldo_modificato: Number(props.detail.value)
            }]
        const movement = {
          ...props.detail,
          payments,
          movimento_ignoto_suggestion: {
            cliente_id: suggestion.cliente_id ?? null,
            natura: suggestion.natura,
            id_rata: suggestion.id_rata ?? null,
            numero_fattura: suggestion.numero_fattura
          }
        }
        if (await service.associateToPayments(movement)) {
          emit('movement-regularized', { id: props.detail.id })
        }
      } finally {
        directAssociating.value = null
      }
    }

    return {
      saving,
      directAssociating,
      loadingSuggestions,
      hasSuggestions,
      suggestions,
      getClientName,
      inCallUrl,
      suggestionKey,
      associa,
      associateSuggestion
    }
  }
}
</script>

<style scoped>
.movimento-ignoto-info {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background-color: #fff7ed;
  color: #9a3412;
}

.title-detail {
  font-size: 10px;
  letter-spacing: 2px;
}

.suggestion-card {
  padding: 0.9rem 1rem;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #f8fbff;
}

.suggestion-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  color: #475569;
  font-size: 0.9rem;
}

.client-incall-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #1683ee;
  font-weight: 600;
  text-decoration: none;
}

.client-incall-link:hover {
  text-decoration: underline;
}
</style>
